'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import type React from 'react'
import {
  type WindowLayout,
  type WindowMode,
  type Viewport,
  STORAGE_KEY,
  defaultLayout,
  parseStoredLayout,
  serializeLayout,
  clampLayout,
  moveFloating,
  resizeFloating,
  dockTargetForPointer,
  windowStyle,
} from '@/lib/ai/window-layout'

function readViewport(): Viewport {
  return { width: window.innerWidth, height: window.innerHeight }
}

function readStored(viewport: Viewport): WindowLayout {
  try {
    return parseStoredLayout(window.localStorage.getItem(STORAGE_KEY), viewport) ?? defaultLayout(viewport)
  } catch {
    return defaultLayout(viewport)
  }
}

function writeStored(layout: WindowLayout) {
  try {
    window.localStorage.setItem(STORAGE_KEY, serializeLayout(layout))
  } catch {
    // Private mode / quota: the window still works, it just forgets its place.
  }
}

/**
 * Floating / docked window state for Biggles. Drag and resize use window-level
 * pointer listeners (the same approach as the draggable DialogContent) so a
 * fast drag cannot outrun the element and drop the gesture.
 */
export function useBigglesWindow() {
  // Null until mounted: server HTML and the first client render must agree,
  // and the stored layout only exists in the browser.
  const [layout, setLayout] = useState<WindowLayout | null>(null)
  const [viewport, setViewport] = useState<Viewport | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const layoutRef = useRef<WindowLayout | null>(null)
  layoutRef.current = layout

  useEffect(() => {
    const vp = readViewport()
    setViewport(vp)
    setLayout(readStored(vp))

    const onResize = () => {
      const next = readViewport()
      setViewport(next)
      setLayout((l) => (l ? clampLayout(l, next) : l))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (layout) writeStored(layout)
  }, [layout])

  const setMode = useCallback((mode: WindowMode) => {
    setLayout((l) => (l ? { ...l, mode } : l))
  }, [])

  const resetPosition = useCallback(() => {
    setLayout(defaultLayout(readViewport()))
  }, [])

  const onHeaderPointerDown = useCallback((e: React.PointerEvent) => {
    // Only the primary button drags; buttons inside the header keep working.
    if (e.button !== 0) return
    if ((e.target as HTMLElement).closest('button, a, input, textarea, select')) return
    const start = layoutRef.current
    if (!start || start.mode !== 'floating') return
    e.preventDefault()

    const origin = { px: e.clientX, py: e.clientY, layout: start }
    setIsDragging(true)

    const onMove = (ev: PointerEvent) => {
      const vp = readViewport()
      setLayout(moveFloating(origin.layout, ev.clientX - origin.px, ev.clientY - origin.py, vp))
    }
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      setIsDragging(false)
      const dock = dockTargetForPointer(ev.clientX, readViewport())
      if (dock) setLayout((l) => (l ? { ...l, mode: dock } : l))
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }, [])

  const onResizeHandlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return
    const start = layoutRef.current
    if (!start || start.mode !== 'floating') return
    e.preventDefault()
    e.stopPropagation()

    const origin = { px: e.clientX, py: e.clientY, layout: start }
    setIsDragging(true)

    const onMove = (ev: PointerEvent) => {
      setLayout(resizeFloating(origin.layout, ev.clientX - origin.px, ev.clientY - origin.py, readViewport()))
    }
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
      setIsDragging(false)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
  }, [])

  const style = layout && viewport ? windowStyle(layout, viewport) : undefined

  return {
    /** Null until mounted; render nothing for the window until it resolves. */
    layout,
    style,
    isDragging,
    setMode,
    resetPosition,
    onHeaderPointerDown,
    onResizeHandlePointerDown,
  }
}
